/**
 * Russian display formatting of the v2 design (oms.js helpers): numbers use a narrow no-break space
 * for thousands, a comma for decimals and a true minus; money puts the ISO code after a no-break
 * space — «1 234,56 USD», «2 200 BYN» — the same for every currency (V81). Inputs are stored units: minor units, rate_e4, minutes, `YYYY-MM-DD`, `YYYY-MM`.
 */

const THIN = ' ';
export const NBSP = ' ';
const MINUS = '−';

export const MONTHS_SHORT_RU: readonly string[] = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
export const MONTHS_GENITIVE_SHORT_RU: readonly string[] = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_PREPOSITIONAL = [
  'январе', 'феврале', 'марте', 'апреле', 'мае', 'июне',
  'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре',
];
const MONTHS_NOMINATIVE = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];
const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** 1234567.5 → "1 234 567,50" with `decimals` digits (U+202F thousands, comma decimals, true minus). */
export function formatNumberRu(value: number, decimals: number): string {
  const [whole = '0', fraction] = Math.abs(value).toFixed(decimals).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
  return `${value < 0 ? MINUS : ''}${grouped}${fraction ? `,${fraction}` : ''}`;
}

/** The currency as the screens show it: its ISO code for every currency (V81). */
export const currencyRu = (currency: string): string => currency;

/** 312843, "USD" → "3 128,43 USD"; null → "—". */
export function moneyRu(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '—';
  return `${formatNumberRu(minor / 100, 2)}${NBSP}${currencyRu(currency)}`;
}

/** Whole units: 312843, "USD" → "3 128 USD". */
export function money0Ru(minor: number | null | undefined, currency: string): string {
  if (minor == null) return '—';
  return `${formatNumberRu(Math.round(minor / 100), 0)}${NBSP}${currencyRu(currency)}`;
}

/** Up to 3 decimals, only as many as needed: 26.625 → "26,625", 36 → "36". */
export function compactDecimalRu(value: number): string {
  const decimals = Math.abs(value * 100 - Math.round(value * 100)) > 1e-6 ? 3 : Math.abs(value - Math.round(value)) > 1e-9 ? 2 : 0;
  return formatNumberRu(value, decimals);
}

export type RateFormat = 'hourly' | 'billed_hourly' | 'monthly' | 'scope' | 'fixed' | 'payroll' | 'revenue_share' | 'profit_share' | 'salary';

export interface RateLike {
  readonly format: RateFormat;
  readonly rateE4?: number | null;
  readonly amountMinor?: number | null;
  readonly shareE6?: number | null;
  readonly currency: string;
}

const SHARE_SCALE = 10_000;

/** 100 000 (×1e6) → "10"; 125 000 → "12,5". */
export const sharePercentRu = (shareE6: number | null | undefined): string => compactDecimalRu((shareE6 ?? 0) / SHARE_SCALE);

/**
 * "26 USD/ч", "2 000 USD/мес", "9 000 USD под ключ" (design `rateStr`), "10 % выручки", "зарплата
 * через юрлицо" (V69) or "60 % зарплаты через юрлицо" with the project's part of it (V151).
 */
export function rateRu(rate: RateLike | null | undefined): string {
  if (!rate) return '—';
  const symbol = currencyRu(rate.currency);
  if (rate.format === 'revenue_share') return `${sharePercentRu(rate.shareE6)}${NBSP}% выручки`;
  if (rate.format === 'profit_share') return `${sharePercentRu(rate.shareE6)}${NBSP}% прибыли`;
  if (rate.format === 'salary') return rate.shareE6 ? `${sharePercentRu(rate.shareE6)}${NBSP}% зарплаты через юрлицо` : 'зарплата через юрлицо';
  if (rate.format === 'hourly') return `${compactDecimalRu((rate.rateE4 ?? 0) / 10_000)}${NBSP}${symbol}/ч`;
  if (rate.format === 'billed_hourly') return `${compactDecimalRu((rate.rateE4 ?? 0) / 10_000)}${NBSP}${symbol}/выст.${NBSP}ч`;
  const amount = compactDecimalRu((rate.amountMinor ?? 0) / 100);
  return rate.format === 'scope' ? `${amount}${NBSP}${symbol} под ключ` : `${amount}${NBSP}${symbol}/мес`;
}

/** At least 2 decimals, more only when needed (up to 4): 26 → "26,00", 26.625 → "26,625". */
export function amountDecimalRu(value: number): string {
  const decimals = [2, 3, 4].find((digits) => Math.abs(value * 10 ** digits - Math.round(value * 10 ** digits)) < 1e-6) ?? 4;
  return formatNumberRu(value, decimals);
}

/**
 * The rate in a table of one pay format (V81): the number and the currency, no «/ч», «/мес» or
 * «под ключ» — the table says which; with kopecks, so it lines up with the money (V91); shares and
 * the salary read as in `rateRu`.
 */
export function rateValueRu(rate: RateLike | null | undefined): string {
  if (!rate) return '—';
  if (rate.format === 'revenue_share' || rate.format === 'profit_share' || rate.format === 'salary') return rateRu(rate);
  const value = rate.format === 'hourly' || rate.format === 'billed_hourly' ? (rate.rateE4 ?? 0) / 10_000 : (rate.amountMinor ?? 0) / 100;
  return `${amountDecimalRu(value)}${NBSP}${currencyRu(rate.currency)}`;
}

/** Minutes → hours with 0 or 1 decimal: 7050 → "117,5"; null → "—". */
export function hoursRu(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  const hours = minutes / 60;
  return formatNumberRu(hours, Math.abs(hours - Math.round(hours)) < 1e-9 ? 0 : 1);
}

const monthIndex = (period: string): number => Number(period.slice(5, 7)) - 1;

/** "2026-09" → "Сен". */
export const monthShortRu = (period: string): string => MONTHS_SHORT_RU[monthIndex(period)] ?? period;
/** "2026-09" → "Сен 2026". */
export const monthLabelRu = (period: string): string => `${monthShortRu(period)} ${period.slice(0, 4)}`;
/** "2026-09" → "сентябрь". */
export const monthNameRu = (period: string): string => MONTHS_NOMINATIVE[monthIndex(period)] ?? period;
/** "2026-09" → "сентябре" (в сентябре). */
export const monthPrepRu = (period: string): string => MONTHS_PREPOSITIONAL[monthIndex(period)] ?? period;
/** "2026-09" → "сентября" (с сентября). */
export const monthGenRu = (period: string): string => MONTHS_GENITIVE[monthIndex(period)] ?? period;

/** "2026-11-05" → "5 ноя"; empty → "—". */
export function dateShortRu(date: string | null | undefined): string {
  if (!date) return '—';
  return `${Number(date.slice(8, 10))}${NBSP}${MONTHS_GENITIVE_SHORT_RU[Number(date.slice(5, 7)) - 1]}`;
}

/** "2026-11-05" → "05.11.2026"; empty → "—". */
export function dateFullRu(date: string | null | undefined): string {
  if (!date) return '—';
  return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`;
}

/** Russian plural: 1 выплата, 2 выплаты, 5 выплат. */
export function pluralRu(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** Genitive of the first name (design `nameGen`): Кирсан → Кирсана, Нина → Нины, Матвей → Матвея. */
export function firstNameGenitiveRu(fullName: string): string {
  const first = fullName.split(' ')[0] ?? fullName;
  if (first.endsWith('а')) return `${first.slice(0, -1)}${/[гкхжчшщ]а$/.test(first) ? 'и' : 'ы'}`;
  if (first.endsWith('я')) return `${first.slice(0, -1)}и`;
  if (first.endsWith('й')) return `${first.slice(0, -1)}я`;
  return `${first}а`;
}
